// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/Requisicao.tsx
//  Listagem de Requisições de Materiais. Busca, status, período, setor,
//  solicitante, ordenação e KPIs rodam NO SERVIDOR; os filtros ficam na URL.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Trash2, ClipboardList, CheckCircle2, Clock,
  Eye, Pencil, PackageCheck, Loader2,
} from 'lucide-react';
import { useRequisicao } from '@/features/operacional/hooks/useRequisicao';
import { calcularStatusRequisicao } from '@/features/operacional/types';
import type { StatusRequisicao } from '@/features/operacional/types';
import type { RequisicaoListagem } from '@/features/operacional/services/requisicaoService';
import { DeleteConfirmDialog } from '@/features/financeiro/components/DeleteConfirmDialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { LocalEstoqueNome } from '@/components/LocalEstoqueNome';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { BarraFiltros } from '@/components/BarraFiltros';
import { BotaoExportar } from '@/components/BotaoExportar';
import { FiltroPeriodo } from '@/components/FiltroPeriodo';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import { SortableTableHead } from '@/components/SortableTableHead';
import { LocalEstoqueSelect } from '@/components/LocalEstoqueSelect';
import { UsuarioSearchCombobox } from '@/components/UsuarioSearchCombobox';
import { useFiltrosNaUrl } from '@/hooks/useFiltrosNaUrl';
import { usePaginacaoServidor } from '@/hooks/usePaginacaoServidor';
import { alternarOrdenacao, type DirecaoOrdenacao } from '@/lib/consultas';

const statusConfig: Record<StatusRequisicao, { label: string; className: string; icon: typeof Clock }> = {
  pendente:  { label: 'Pendente',  className: 'bg-blue-500/10 text-blue-500',   icon: Clock        },
  aprovada:  { label: 'Aprovada',  className: 'bg-orange-500/10 text-orange-500', icon: CheckCircle2 },
  realizada: { label: 'Realizada', className: 'bg-green-500/10 text-green-500', icon: PackageCheck  },
};

const vazio = <span className="text-muted-foreground">—</span>;

type Campo = 'numero' | 'movimento' | 'agendamento';

const PADROES = {
  busca: '',
  status: 'todos',
  dtInicio: '',
  dtFim: '',
  setorId: 0,
  solicitanteId: 0,
  ordenarPor: 'movimento',
  direcao: 'desc',
  pagina: 1,
  tamanhoPagina: 20,
};

const Requisicao = () => {
  const navigate = useNavigate();
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const { filtros, setFiltros, limparFiltros, filtrosAtivos } = useFiltrosNaUrl(PADROES);
  const pag = usePaginacaoServidor({
    controlado: { pagina: filtros.pagina, tamanhoPagina: filtros.tamanhoPagina, onChange: setFiltros },
  });

  const { requisicoes, total, totalPaginas, isLoading, isFetching, deleteRequisicao, updateRequisicao, resumo } =
    useRequisicao({
      pagina: pag.pagina,
      tamanhoPagina: pag.tamanhoPagina,
      filtros: {
        busca: filtros.busca, status: filtros.status, dtInicio: filtros.dtInicio, dtFim: filtros.dtFim,
        setorId: filtros.setorId, solicitanteId: filtros.solicitanteId,
        ordenarPor: filtros.ordenarPor, direcao: filtros.direcao as DirecaoOrdenacao,
      },
    });

  const ordenacao = { ordenarPor: filtros.ordenarPor, direcao: filtros.direcao as DirecaoOrdenacao };
  const onOrdenar = (campo: Campo) => setFiltros(alternarOrdenacao(ordenacao, campo, 'desc'));
  const cabecalho = { ...ordenacao, onOrdenar };
  const kpi = (v?: number) => (resumo ? v ?? 0 : '…');

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Requisições de Materiais</h1>
          <p className="text-muted-foreground mt-1">Gerencie as requisições de materiais para produção</p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/operacional/requisicoes/nova')}>
          <Plus className="h-4 w-4" />Nova Requisição
        </Button>
      </div>

      {/* KPIs — calculados no servidor sobre o filtro (todas as páginas) */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" />Pendentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500">{kpi(resumo?.pendentes)}</div>
            <p className="text-xs text-muted-foreground mt-1">Aguardando aprovação</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />Aprovadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-500">{kpi(resumo?.aprovadas)}</div>
            <p className="text-xs text-muted-foreground mt-1">Aguardando atendimento</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <PackageCheck className="h-4 w-4" />Realizadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{kpi(resumo?.realizadas)}</div>
            <p className="text-xs text-muted-foreground mt-1">Atendidas</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <ClipboardList className="h-4 w-4" />Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{kpi(resumo?.total)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {filtrosAtivos > 0 ? 'requisições no filtro' : 'requisições cadastradas'}
            </p>
          </CardContent>
        </Card>
      </div>

      <BarraFiltros
        busca={filtros.busca}
        onBuscar={(busca) => setFiltros({ busca })}
        placeholderBusca="Nº ou observação..."
        filtrosAtivos={filtrosAtivos}
        onLimpar={limparFiltros}
        acoes={<BotaoExportar endpoint="/api/requisicoes/exportar" params={filtros} />}
      >
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select value={filtros.status} onValueChange={(status) => setFiltros({ status })}>
            <SelectTrigger className="h-9 w-[150px]" aria-label="Status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="pendente">Pendente</SelectItem>
              <SelectItem value="aprovada">Aprovada</SelectItem>
              <SelectItem value="realizada">Realizada</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <FiltroPeriodo
          rotulo="Movimento"
          inicio={filtros.dtInicio}
          fim={filtros.dtFim}
          onChange={({ inicio, fim }) => setFiltros({ dtInicio: inicio, dtFim: fim })}
        />
        <div className="space-y-1 w-52">
          <Label className="text-xs text-muted-foreground">Setor</Label>
          <LocalEstoqueSelect
            value={filtros.setorId || null}
            onChange={(id) => setFiltros({ setorId: id ?? 0 })}
            rotuloTodos="Todos os setores"
            className="h-9"
          />
        </div>
        <div className="space-y-1 w-56">
          <Label className="text-xs text-muted-foreground">Solicitante</Label>
          <UsuarioSearchCombobox
            value={filtros.solicitanteId || undefined}
            onChange={(id) => setFiltros({ solicitanteId: id || 0 })}
            placeholder="Todos os solicitantes"
          />
        </div>
        {isFetching && !isLoading && (
          <Loader2 className="h-4 w-4 mb-2.5 animate-spin text-muted-foreground" aria-label="Atualizando" />
        )}
      </BarraFiltros>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead campo="numero" {...cabecalho} className="w-20">Nº</SortableTableHead>
                  <SortableTableHead campo="movimento" {...cabecalho}>Data Movimento</SortableTableHead>
                  <SortableTableHead campo="agendamento" {...cabecalho}>Agendamento</SortableTableHead>
                  <TableHead>Setor</TableHead>
                  <TableHead>Solicitante</TableHead>
                  <TableHead>Observação</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
                ) : requisicoes.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">{filtros.dtInicio || filtros.dtFim ? 'Nenhuma requisição neste período — amplie o período para ver as anteriores' : 'Nenhuma requisição encontrada'}</TableCell></TableRow>
                ) : (
                  requisicoes.map((r: RequisicaoListagem) => {
                    const status = calcularStatusRequisicao(r);
                    const cfg    = statusConfig[status];
                    const Icon   = cfg.icon;
                    return (
                      <TableRow key={r.id}>
                        <TableCell className="font-medium">{r.id}</TableCell>
                        <TableCell>{new Date(r.dtMovimento).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell>
                          {new Date(r.dtAgendamento).toLocaleDateString('pt-BR')}
                          {r.horaAgendamento && <span className="text-muted-foreground ml-1 text-xs">{r.horaAgendamento}</span>}
                        </TableCell>
                        <TableCell>{r.setorNome ?? <LocalEstoqueNome id={r.setorSolicitacaoId} />}</TableCell>
                        <TableCell>{r.solicitanteNome || vazio}</TableCell>
                        <TableCell className="max-w-xs truncate">{r.observacao || vazio}</TableCell>
                        <TableCell>
                          <Badge className={cfg.className}>
                            <span className="flex items-center gap-1"><Icon className="h-3 w-3" />{cfg.label}</span>
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {!r.isRealizada && (
                              <Button size="sm" variant="ghost"
                                onClick={() => navigate(`/operacional/requisicoes/${r.id}`)} title="Editar">
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" title="Ver detalhes"
                              onClick={() => navigate(`/operacional/requisicoes/${r.id}`)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            {status === 'pendente' && (
                              <Button size="sm" variant="ghost"
                                onClick={() => updateRequisicao({ id: r.id, data: { isAprovada: true } })}
                                title="Aprovar">
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                              </Button>
                            )}
                            {!r.isRealizada && (
                              <Button size="sm" variant="ghost" onClick={() => setDeletingId(r.id)} title="Excluir">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <PaginacaoRodape {...pag.rodape({ total, totalPaginas })} />

      <DeleteConfirmDialog
        open={!!deletingId}
        onOpenChange={(open) => !open && setDeletingId(null)}
        onConfirm={() => { if (deletingId) { deleteRequisicao(deletingId); setDeletingId(null); } }}
        title="Excluir Requisição"
        description="Tem certeza que deseja excluir esta requisição de materiais?"
      />
    </div>
  );
};

export default Requisicao;
